import React from "react";

type Props = {
  children: React.ReactNode;
};

/**
 * Atmospheric background for the /create/* onboarding wizard: soft brand-family
 * blur orbs (indigo / deep-indigo / cool neutral — ADR 0057 tokens only) over
 * the app ground, plus a near-invisible grid for canvas texture. Pure
 * background + flex wrapper — CreateOrganizationLayout owns the ONE header,
 * auth gate, and usage tracker (the shell's old second header/rightLabel pill
 * is gone; step labels live in WizardStepPill on each page). min-h accounts
 * for the layout's h-16 header so the orbs fill the viewport without a second
 * nested min-h-screen.
 */
export default function CreateOnboardingShell({ children }: Props) {
  return (
    // No overflow-hidden here: an overflow ancestor disables position:sticky
    // for every descendant (the design step's sticky Live Preview). The blur
    // orbs that used to need the clip are clipped by their own background
    // layer below instead.
    <div className="relative flex min-h-[calc(100vh-4rem)] w-full flex-1 flex-col bg-background">
      {/* Gradient mesh background — brand family only */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-[20%] -top-[30%] h-[600px] w-[600px] rounded-full bg-gradient-to-br from-primary/25 via-primary/10 to-transparent blur-[100px]" />
        <div className="absolute -right-[15%] top-[20%] h-[500px] w-[500px] rounded-full bg-gradient-to-bl from-primary-deep/20 via-primary/10 to-transparent blur-[100px]" />
        <div className="absolute bottom-0 left-[30%] h-[400px] w-[400px] rounded-full bg-gradient-to-t from-muted/60 via-muted/30 to-transparent blur-[100px]" />
      </div>

      {/* Subtle grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.4]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.015) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}
      />

      {children}
    </div>
  );
}
